import React, {useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {DropdownMenu} from 'components/ui-kit/DropdownMenu';
import {codesGenerated} from 'models/orders/detail';
import {PackageTypes, TRANSPORT_PACKAGE_TYPES} from 'types/gtins';
import {FormattedLabelsForm} from 'types/orders';

import {PrintModal} from '../PrintModal';
import {useCodeExportJob} from '../useCodeExportJob';

interface Props {
    orderId: string;
    gtin: string;
    packageType: PackageTypes;
    printed?: boolean;
    status?: string;
}

export const CodeExportControl = ({orderId, gtin, packageType, printed, status}: Props) => {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [format, setFormat] = useState<FormattedLabelsForm['format']>('PDF');
    const job = useCodeExportJob((request) => {
        if (['PDF', 'EPS'].includes(request.format)) codesGenerated();
    });

    useEffect(() => {
        job.cancel();
        setOpen(false);
    }, [orderId, gtin, packageType]);

    const canPrint = status !== 'DECLINED' && !printed;
    useEffect(() => {
        if (!canPrint && !job.busy && !job.canResume) setOpen(false);
    }, [canPrint, job.busy, job.canResume]);

    const formatted = ['PDF', 'EPS'].includes(format);
    const specificFormat =
        TRANSPORT_PACKAGE_TYPES.includes(packageType) || packageType === PackageTypes.CUSTOMS_UNIT;

    const download = async (
        selectedFormat: FormattedLabelsForm['format'],
        pageFormat: FormattedLabelsForm['pageFormat'] = 'LETTER',
    ) => {
        if (!orderId || job.busy) return;
        const isFormatted = ['PDF', 'EPS'].includes(selectedFormat);
        if (isFormatted && !canPrint && !job.canResume) return;
        setFormat(selectedFormat);
        setOpen(true);
        const success = await job.run({
            orderId,
            gtin,
            format: selectedFormat,
            ...(isFormatted ? {pageFormat, showHri: false} : {}),
        });
        if (success) setOpen(false);
    };

    const choose = (selectedFormat: FormattedLabelsForm['format']) => {
        if (job.busy || job.canResume || !orderId) return;
        if (['XLS', 'CSV'].includes(selectedFormat)) {
            download(selectedFormat);
        } else if (canPrint) {
            job.cancel();
            setFormat(selectedFormat);
            setOpen(true);
        }
    };

    const close = () => {
        if (!job.busy) setOpen(false);
    };

    return (
        <>
            {job.canResume ? (
                <Button
                    size="sm"
                    variant={ButtonVariant.Secondary}
                    disabled={job.busy}
                    style={{width: 'auto'}}
                    onClick={() => setOpen(true)}
                >
                    {t('pages.orders.print.submit')}
                </Button>
            ) : (
                <DropdownMenu
                    label={t('pages.orders.print.submit')}
                    width={109}
                    buttonVariant={ButtonVariant.Secondary}
                    buttonSize="sm"
                    disabled={!orderId || job.busy}
                >
                    <DropdownMenu.List>
                        {(canPrint ? ['PDF', 'EPS', 'XLS', 'CSV'] : ['XLS', 'CSV']).map((value) => (
                            <DropdownMenu.Item
                                key={value}
                                onSelect={() => choose(value as FormattedLabelsForm['format'])}
                            >
                                {value}
                            </DropdownMenu.Item>
                        ))}
                    </DropdownMenu.List>
                </DropdownMenu>
            )}
            <PrintModal
                show={open}
                onClose={close}
                onConfirm={(pageFormat) => download(format, pageFormat)}
                specificFormat={specificFormat}
                choosePageFormat={formatted}
                busy={job.busy}
                canResume={job.canResume}
                phase={job.phase}
                error={job.error}
            />
        </>
    );
};
